import { Person, StringType, Stringing, StringingEntry, Racquet } from './types';

// Accept absolute URLs (https://...), or a path like "/api", or a bare host path like "api"
const RAW = (process.env.REACT_APP_API_BASE ?? '/api').trim();

function normalizeBase(raw: string): string {
  if (/^https?:\/\//i.test(raw)) {
    // absolute URL already
    return raw.replace(/\/+$/, ''); // no trailing slash
  }
  // treat anything else as path on current origin (e.g., "/api" or "api")
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${window.location.origin}${path}`.replace(/\/+$/, '');
}

const BASE = normalizeBase(RAW);

function apiUrl(path: string) {
  const rel = String(path).replace(/^\/+/, '');
  return new URL(rel, `${BASE}/`).toString();
}

const buildHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'x-api-key': process.env.REACT_APP_API_KEY || '',
});

// Fetch all stringings
export async function getAllStringings(): Promise<StringingEntry[]> {
  const res = await fetch(apiUrl('/stringings'), {
    headers: buildHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch stringings');
  return res.json();
}

// Fetch all persons
export async function getAllPersons(options?: { sortBy?: 'recent' | 'frequent' }): Promise<Person[]> {
  const url = new URL(apiUrl('/persons'));
  if (options?.sortBy) {
    url.searchParams.append('sortBy', options.sortBy);
  }
  const res = await fetch(url.toString(), {
    headers: buildHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch persons');
  return res.json();
}

// Fetch all string types
export async function getAllStringTypes(): Promise<StringType[]> {
  const res = await fetch(apiUrl('/strings'), {
    headers: buildHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch string types');
  return res.json();
}

// Create a new person
export async function createPerson(data: Omit<Person, 'id'>): Promise<Person> {
  const res = await fetch(apiUrl('/persons'), {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create person');
  return res.json();
}

// Create a new string type
export async function createString(data: Omit<StringType, 'id'>): Promise<StringType> {
  const res = await fetch(apiUrl('/strings'), {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create string');
  return res.json();
}

// Create a new stringing
export async function createStringing(data: Omit<Stringing, 'id'>): Promise<Stringing> {
  const res = await fetch(apiUrl('/stringings'), {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create stringing');
  return res.json();
}

// Update an existing stringing
export async function updateStringing(data: Stringing): Promise<Stringing> {
  const res = await fetch(apiUrl(`/stringings/${data.id}`), {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update stringing');
  return res.json();
}

// Update an existing string type
export async function updateStringType(id: string, data: Partial<StringType>): Promise<StringType> {
  const res = await fetch(apiUrl(`/strings/${id}`), {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update string type');
  return res.json();
}

// Fetch all racquets
export async function getAllRacquets(): Promise<Racquet[]> {
  const res = await fetch(apiUrl('/racquets'), {
    headers: buildHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch racquets');
  return res.json();
}

// Create a new racquet
export async function createRacquet(data: { brand: string; model: string }): Promise<Racquet> {
  const res = await fetch(apiUrl('/racquets'), {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create racquet');
  return res.json();
}

// Update racquet
export async function updateRacquet(id: string, data: Partial<{ brand: string; model: string }>): Promise<Racquet> {
  const res = await fetch(apiUrl(`racquets/${id}`), {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update racquet');
  return res.json();
}
